// AdminPage.tsx — Co Marca Admin Dashboard — con Ventas de Cafetería integradas
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { isReservationCancelled } from '../../utils/reservationStatus';
import { fetchCitas, fetchClientesRetorno, Cita, ClienteRetorno } from '../../services/citasService';
import { fetchSucursales, Sucursal } from '../../services/pos/sucursalService';
import { fetchPuntosDeVenta, PuntoDeVenta } from '../../services/pos/puntoDeVentaService';
import { fetchSesionesPOS, SesionPOS } from '../../services/pos/sesionPOSService';
import { fetchAdministrativeReport } from '../../services/administrativeReportsService';
import { boliviaDateStamp, buildDashboardFinancials, DashboardPayment, parseDashboardPaymentRows } from './dashboardFinancials';
import {
    Banknote, BarChart2, TrendingUp, Users, DollarSign,
    Calendar, CheckCircle, AlertCircle, PieChart, Target, User,
    Search, RefreshCcw, Building, Filter, ChevronDown, Clock,
} from 'lucide-react';
import {
    ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
    BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { format, subDays, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import Loading from '../Loading';

// ─── Helpers ────────────────────────────────────────────────────────────────
const parsearFechaDesdeInput = (f: string): Date => {
    const [y, m, d] = f.split('-').map(Number);
    return new Date(y, m - 1, d);
};
const convertirFechaServidorALocal = (s: string): Date => {
    if (!s) return new Date();
    const f = new Date(s);
    return new Date(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate(),
        f.getUTCHours(), f.getUTCMinutes(), f.getUTCSeconds());
};
const formatoFechaLocal = (s?: string | null) => {
    if (!s) return '-';
    try { return format(convertirFechaServidorALocal(s), "d 'de' MMMM, yyyy", { locale: es }); } catch { return s; }
};
const formatoFechaCorta = (s?: string | null) => {
    if (!s) return '-';
    try { return format(convertirFechaServidorALocal(s), 'dd/MM/yyyy', { locale: es }); } catch { return s; }
};
const formatoFechaGrafico = (s?: string | null) => {
    if (!s) return '-';
    try { return format(convertirFechaServidorALocal(s), 'dd/MM', { locale: es }); } catch { return s; }
};
const formatoMoneda = (n: number) =>
    new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB', minimumFractionDigits: 2 }).format(n);

// ─── Colores Co Marca ───────────────────────────────────────────────────────
const colors = {
    primary:       '#c1348b',
    primaryDark:   '#8f1f64',
    secondary:     '#61257b',
    secondaryLight:'#8b3fa8',
    accent:        '#ffd97c',
    accentDark:    '#ea8406',
};

// ─── CSS ─────────────────────────────────────────────────────────────────────
const getCss = (isDark: boolean) => `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

.adm-dash { font-family: 'DM Sans', sans-serif; color: ${isDark ? '#f1f5f9' : '#0f172a'}; }

.custom-scroll::-webkit-scrollbar { width: 6px; }
.custom-scroll::-webkit-scrollbar-track { background: transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color: rgba(97, 37, 123,0.3); border-radius: 20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(97, 37, 123,0.5); }

@keyframes sectionFadeUp {
    from { opacity:0; transform:translateY(22px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(193, 52, 139,.35); }
    50%     { box-shadow:0 0 0 5px rgba(193, 52, 139,0); }
}
@keyframes blobFloat {
    0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
    50%     { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes spin { to { transform:rotate(360deg); } }
@keyframes slideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }

.adm-fadeup   { animation: sectionFadeUp .55s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-1 { animation: sectionFadeUp .55s .08s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-2 { animation: sectionFadeUp .55s .16s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-3 { animation: sectionFadeUp .55s .24s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-4 { animation: sectionFadeUp .55s .32s cubic-bezier(.16,1,.3,1) both; }

.adm-card {
    background: ${isDark ? '#150a28' : '#fff'};
    border-radius: 20px;
    border: 1.5px solid ${isDark ? 'rgba(193, 52, 139, 0.28)' : '#f1f5f9'};
    transition: all .3s cubic-bezier(.16,1,.3,1);
    color: ${isDark ? '#f1f5f9' : '#0f172a'};
}
.adm-card:hover {
    transform: translateY(-3px);
    box-shadow: ${isDark ? '0 20px 48px rgba(0,0,0,.6)' : '0 20px 48px rgba(0,0,0,.07)'};
}

.adm-metric-card {
    background: ${isDark ? '#150a28' : '#fff'};
    border-radius: 20px;
    border: 1.5px solid ${isDark ? 'rgba(193, 52, 139, 0.28)' : '#f1f5f9'};
    padding: 22px 24px;
    transition: all .3s cubic-bezier(.16,1,.3,1);
    position: relative; overflow: hidden;
    color: ${isDark ? '#f1f5f9' : '#0f172a'};
}
.adm-metric-card::before {
    content:''; position:absolute; top:0; left:0; right:0;
    height: 3px;
    opacity: 0; transition: opacity .3s;
}
.adm-metric-card:hover { transform:translateY(-3px); box-shadow:${isDark ? '0 20px 48px rgba(0,0,0,.6)' : '0 20px 48px rgba(0,0,0,.07)'}; }
.adm-metric-card:hover::before { opacity:1; }

.adm-pill-btn {
    padding: 7px 16px; border-radius: 100px; font-size: .8rem; font-weight: 700;
    background: ${isDark ? 'rgba(255,255,255,0.08)' : '#fff'};
    border: 1.5px solid ${isDark ? 'rgba(193, 52, 139, 0.35)' : '#e2e8f0'};
    color: ${isDark ? '#e2e8f0' : '#374151'};
    cursor: pointer;
    font-family: 'DM Sans', sans-serif; transition: all .2s;
}
.adm-pill-btn:hover {
    background: ${colors.primary}; color:#fff; border-color:${colors.primary};
    box-shadow: 0 4px 12px ${colors.primary}28;
}

.adm-input {
    width:100%; padding:9px 14px; border-radius:10px;
    border:1.5px solid ${isDark ? 'rgba(193, 52, 139, 0.35)' : '#e5e7eb'};
    background: ${isDark ? '#1a0d30' : '#fafafa'};
    font-size:.85rem; color: ${isDark ? '#f8fafc' : '#374151'}; font-family:'DM Sans',sans-serif;
    outline:none; transition:all .2s;
}
.adm-input:focus { border-color:${colors.primary}; box-shadow:0 0 0 3px ${colors.primary}12; background: ${isDark ? '#21103d' : '#fff'}; }

.adm-slidedown { animation: slideDown .2s ease-out; }

.adm-row-hover { transition:background .15s; }
.adm-row-hover:hover { background: ${isDark ? 'rgba(193,52,139,0.15)' : '#f8fafc'}; }
`;

// ─── Tooltips ────────────────────────────────────────────────────────────────
const TooltipBarra = ({ active, payload, label, isDark }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: isDark ? '#1a0c32' : '#fff',
            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : '1.5px solid #f1f5f9',
            borderRadius:'14px', padding:'12px 16px',
            boxShadow:'0 8px 32px rgba(0,0,0,.35)', fontFamily:"'DM Sans',sans-serif"
        }}>
            <p style={{ fontWeight:'700', color: isDark ? '#ffd97c' : '#0f172a', marginBottom:'6px', fontSize:'.82rem' }}>{label}</p>
            {payload.map((p: any, i: number) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:p.color }}/>
                    <span style={{ fontSize:'.78rem', color: isDark ? '#cbd5e1' : '#64748b' }}>{p.name}</span>
                    <span style={{ fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', marginLeft:'auto', fontSize:'.82rem' }}>{formatoMoneda(p.value)}</span>
                </div>
            ))}
        </div>
    );
};
const TooltipPie = ({ active, payload, isDark }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{
            background: isDark ? '#1a0c32' : '#fff',
            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : '1.5px solid #f1f5f9',
            borderRadius:'14px', padding:'12px 16px',
            boxShadow:'0 8px 32px rgba(0,0,0,.35)', fontFamily:"'DM Sans',sans-serif"
        }}>
            <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'4px' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:d.color }}/>
                <span style={{ fontWeight:'700', color: isDark ? '#ffd97c' : '#0f172a', fontSize:'.82rem' }}>{d.name}</span>
            </div>
            <p style={{ fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a', fontSize:'1.1rem' }}>{formatoMoneda(d.value)}</p>
        </div>
    );
};

// ─── Componente principal ────────────────────────────────────────────────────
const StatsPanel = () => {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const css = useMemo(() => getCss(isDark), [isDark]);

    const [citas, setCitas]                           = useState<Cita[]>([]);
    const [isLoading, setIsLoading]                   = useState(true);
    const [isLoadingPayments, setIsLoadingPayments]   = useState(false);
    const [paymentError, setPaymentError]             = useState('');

    const [clientesRetorno, setClientesRetorno]       = useState<ClienteRetorno[]>([]);
    const [loadingClientes, setLoadingClientes]       = useState(false);
    const [sucursales, setSucursales]                 = useState<Sucursal[]>([]);
    const [sucursalesActivas, setSucursalesActivas]   = useState<Sucursal[]>([]);
    const [sucursalSeleccionada, setSucursalSel]      = useState('todas');
    const [puntosDeVenta, setPuntosDeVenta]           = useState<PuntoDeVenta[]>([]);
    const [sesionesPOS, setSesionesPOS]               = useState<SesionPOS[]>([]);
    const [pagos, setPagos]                           = useState<DashboardPayment[]>([]);
    const [cafeteria, setCafeteria] = useState({ ventas: 0, total: 0, pagado: 0, saldo: 0 });
    const [pagosHoy, setPagosHoy]                     = useState<DashboardPayment[]>([]);
    const [mostrarSelectorSucursal, setMostrarSel]   = useState(false);
    const [fechaInicio, setFechaInicio]               = useState('');
    const [fechaFin, setFechaFin]                     = useState('');
    const [busqueda, setBusqueda]                     = useState('');
    const [pagosRecientes, setPagosRecientes]         = useState<DashboardPayment[]>([]);

    const selectorRef   = useRef<HTMLDivElement>(null);
    const btnSucRef     = useRef<HTMLButtonElement>(null);

    const [stats, setStats] = useState({
        totalCitas:0, totalPagado:0, promedioTicket:0, pagosRegistrados:0,
        deudaTotal:0, clientesActivos:0, citasHoy:0, totalPagadoHoy:0,
    });
    const [metrica, setMetrica] = useState({
        ingresosPorDia:[] as Array<{ fecha: string; ingresos: number }>,
        distribucionOrigen:[] as Array<{ name:string; value:number; color:string; count:number }>,
    });

    const formatDateForInput = (d: Date) => {
        const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dd = String(d.getDate()).padStart(2,'0');
        return `${y}-${m}-${dd}`;
    };


    useEffect(() => {
        const h = (e: MouseEvent) => { if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) setMostrarSel(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    useEffect(() => { const hoy = boliviaDateStamp(); setFechaInicio(hoy); setFechaFin(hoy); }, []);

    useEffect(() => {
        fetchSucursales().then(d => { setSucursales(d); setSucursalesActivas(d.filter((s:Sucursal) => s.activo)); }).catch(console.error);
    }, []);

    const filtrarPorSucursal = useCallback((list: Cita[], sid: string) => {
        if (sid === 'todas') return list;
        const n = parseInt(sid);
        return list.filter(c => {
            const ses = sesionesPOS.find(s => s.id === c.sesion_pos);
            if (!ses) return false;
            const pto = puntosDeVenta.find(p => p.id === ses.punto_de_venta);
            return pto?.sucursal === n;
        });
    }, [sesionesPOS, puntosDeVenta]);

    const aplicarFiltro = useCallback(() => {
        if (!fechaInicio || !fechaFin) return;
        const enPeriodoCitas = citas.filter(c => {
            if (!c.fecha_inicio) return false;
            const day = String(c.fecha_inicio).slice(0, 10);
            return day >= fechaInicio && day <= fechaFin;
        });
        const filtradasCitas = filtrarPorSucursal(enPeriodoCitas, sucursalSeleccionada);
        const financial = buildDashboardFinancials(pagos);
        const todayFinancial = buildDashboardFinancials(pagosHoy);
        const hoy = boliviaDateStamp();
        const citasHoy = citas.filter(c => {
            if (!c.fecha_inicio) return false;
            return String(c.fecha_inicio).slice(0, 10) === hoy;
        });
        let deudaTotal = 0;
        filtradasCitas.forEach(c => { const total=Number(c.total)||0; deudaTotal += Math.max(0, total-(Number(c.monto_pagado)||0)); });
        setPagosRecientes([...pagos].sort((a,b)=>Date.parse(b.paidAt)-Date.parse(a.paidAt)).slice(0,100));
        setStats({
            totalCitas: filtradasCitas.length,
            totalPagado: Number(financial.total),
            promedioTicket: Number(financial.average),
            pagosRegistrados: financial.paymentCount,
            deudaTotal,
            clientesActivos: financial.uniquePayers,
            citasHoy: (sucursalSeleccionada === 'todas' ? citasHoy : filtrarPorSucursal(citasHoy, sucursalSeleccionada)).length,
            totalPagadoHoy: Number(todayFinancial.total),
        });
        setMetrica({
            ingresosPorDia: financial.byDay.map(day=>({ fecha: formatoFechaGrafico(day.date), ingresos:Number(day.amount) })),
            distribucionOrigen: financial.byOrigin.map((origin,index)=>({
                name:origin.label, value:Number(origin.amount), count:origin.count,
                color:[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight][index % 4],
            })),
        });
    }, [citas, pagos, pagosHoy, fechaInicio, fechaFin, sucursalSeleccionada, filtrarPorSucursal]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            fetchCitas(),
            fetchClientesRetorno(),
            fetchPuntosDeVenta(),
            fetchSesionesPOS(),
        ])
            .then(([c, cl, pt, ses]) => {
                setCitas(c.filter(item => !isReservationCancelled(item)));
                setClientesRetorno(cl);
                setPuntosDeVenta(pt);
                setSesionesPOS(ses);
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (!fechaInicio || !fechaFin) {
            setCafeteria({ ventas: 0, total: 0, pagado: 0, saldo: 0 });
            setPagos([]);
            setPagosHoy([]);
            setPagosRecientes([]);
            setStats({
                totalCitas:0, totalPagado:0, promedioTicket:0, pagosRegistrados:0,
                deudaTotal:0, clientesActivos:0, citasHoy:0, totalPagadoHoy:0,
            });
            setMetrica({ ingresosPorDia:[], distribucionOrigen:[] });
            setPaymentError('');
            setIsLoadingPayments(false);
            return;
        }
        let cancelled = false;
        const branch = sucursalSeleccionada === 'todas' ? {} : { sucursal_id: Number(sucursalSeleccionada) };
        const periodFilters = { fecha_desde: fechaInicio, fecha_hasta: fechaFin, ...branch };
        const today = boliviaDateStamp();
        const periodRequest = fetchAdministrativeReport('pagos', periodFilters);
        const todayRequest = fetchAdministrativeReport('pagos', { fecha_desde: today, fecha_hasta: today, ...branch });
        setIsLoadingPayments(true);
        setPaymentError('');
        Promise.all([periodRequest, todayRequest, fetchAdministrativeReport('cafeteria', periodFilters)])
            .then(([period, current, sales]) => {
                if (cancelled) return;
                setCafeteria({ ventas: Number(sales.summary.ventas) || 0, total: Number(sales.summary.total) || 0, pagado: Number(sales.summary.pagado) || 0, saldo: Number(sales.summary.saldo) || 0 });
                setPagos(parseDashboardPaymentRows(period.rows));
                setPagosHoy(parseDashboardPaymentRows(current.rows));
            })
            .catch(error => {
                if (cancelled) return;
                console.error(error);
                setCafeteria({ ventas: 0, total: 0, pagado: 0, saldo: 0 });
            setPagos([]);
                setPagosHoy([]);
                setPaymentError('No se pudo validar la información financiera.');
            })
            .finally(() => { if (!cancelled) setIsLoadingPayments(false); });
        return () => { cancelled = true; };
    }, [fechaInicio, fechaFin, sucursalSeleccionada]);

    useEffect(() => { if (fechaInicio && fechaFin) aplicarFiltro(); }, [citas, pagos, pagosHoy, fechaInicio, fechaFin, sucursalSeleccionada, aplicarFiltro]);

    const loadClientes = useCallback(() => { setLoadingClientes(true); fetchClientesRetorno().then(setClientesRetorno).catch(console.error).finally(()=>setLoadingClientes(false)); }, []);
    useEffect(() => { loadClientes(); }, [loadClientes]);

    const getNombreSucursal = () => {
        if (sucursalSeleccionada==='todas') return 'Todas las sucursales';
        const s = sucursales.find(x=>x.id.toString()===sucursalSeleccionada);
        return s ? s.nombre : 'Sucursal desconocida';
    };

    const clientesFrecuentes = useMemo(() => [...clientesRetorno].sort((a,b)=>b.cantidad_citas-a.cantidad_citas).slice(0,5), [clientesRetorno]);
    const financials = useMemo(() => buildDashboardFinancials(pagos), [pagos]);

    const handleToggleSuc = () => setMostrarSel(v => !v);


    // ── Loading inline ──
    if (isLoading || isLoadingPayments) return <Loading text="Cargando dashboard financiero..." />;

    // ── Métricas principales ──
    const metricCards = [
        { label:'Ventas de Cafetería', value:String(cafeteria.ventas), sub:`Vendido: ${formatoMoneda(cafeteria.total)}`, subColor:colors.secondary, icon:<CheckCircle size={18}/>, accentColor:colors.secondary, gradFrom:'#f0f9ff', gradTo:'#dbeafe' },
        { label:'Cobrado en Cafetería', value:formatoMoneda(pagos.filter(p => p.origin === 'cafeteria').reduce((sum, p) => sum + Math.round(Number(p.amount) * 100), 0) / 100), sub:'Incluido en Total Cobrado', subColor:colors.secondary, icon:<CheckCircle size={18}/>, accentColor:colors.secondary, gradFrom:'#f0f9ff', gradTo:'#dbeafe' },
        { label:'Total Cobrado',        value:formatoMoneda(stats.totalPagado), sub:'Pagos confirmados del período', subColor:colors.secondaryLight, icon:<Banknote size={18}/>, accentColor:colors.secondaryLight, gradFrom:'#ecfdf5', gradTo:'#d1fae5' },
        { label:'Pagos Registrados',    value:String(stats.pagosRegistrados), sub:getNombreSucursal(), subColor:colors.secondary, icon:<CheckCircle size={18}/>, accentColor:colors.secondary, gradFrom:'#f0f9ff', gradTo:'#dbeafe' },
        { label:'Ticket Promedio',      value:formatoMoneda(stats.promedioTicket), sub:'Por pago registrado', subColor:colors.accentDark, icon:<TrendingUp size={18}/>, accentColor:colors.accentDark, gradFrom:'#fffbeb', gradTo:'#fef3c7' },
        { label:'Deuda Pendiente',      value:formatoMoneda(stats.deudaTotal + cafeteria.saldo),     sub:'Por cobrar', subColor:colors.primary, icon:<AlertCircle size={18}/>, accentColor:colors.primary, gradFrom:'#fff5f6', gradTo:'#ffe4e6' },
        { label:'Pagadores Identificados', value:String(stats.clientesActivos), sub:'Clientes únicos del período', subColor:colors.accentDark, icon:<Users size={18}/>, accentColor:colors.accentDark, gradFrom:'#fef7e0', gradTo:'#fde9c0' },
    ];

    return (
        <>
            <style>{css}</style>
            <div className="adm-dash" style={{ background: isDark ? 'transparent' : '#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── HEADER ── */}
                <div className="adm-fadeup" style={{ position:'relative', background: isDark ? '#150a28' : '#fff', borderRadius:'24px', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1.5px solid #f1f5f9', boxShadow: isDark ? '0 8px 32px rgba(0,0,0,.5)' : '0 4px 24px rgba(0,0,0,.04)', padding:'clamp(24px,3vw,36px)', marginBottom:'28px', overflow:'hidden' }}>
                    <div style={{ position:'absolute', top:'-60px', right:'-60px', width:'260px', height:'260px', background:`linear-gradient(135deg,${colors.primary}12,${colors.secondary}0a)`, borderRadius:'60% 40% 30% 70%/60% 30% 70% 40%', animation:'blobFloat 7s ease-in-out infinite', pointerEvents:'none', zIndex:0 }}/>
                    <div style={{ position:'absolute', bottom:'20px', left:'20px', display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'6px', opacity:.07, pointerEvents:'none', zIndex:0 }}>
                        {Array.from({length:25}).map((_,i)=><div key={i} style={{ width:'4px', height:'4px', borderRadius:'50%', background: isDark ? '#f8fafc' : '#0f172a' }}/>)}
                    </div>

                    <div style={{ position:'relative', zIndex:1 }}>
                        <div style={{ display:'flex', flexWrap:'wrap', alignItems:'flex-start', justifyContent:'space-between', gap:'16px', marginBottom:'24px' }}>
                            <div>
                                <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'10px' }}>
                                    <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:`${colors.primary}10`, border:`1.5px solid ${colors.primary}22`, borderRadius:'100px', padding:'4px 14px', fontSize:'.72rem', fontWeight:'800', color:colors.primaryDark, letterSpacing:'.06em', textTransform:'uppercase' }}>
                                        <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:colors.primary, display:'inline-block', animation:'pulseD 2s infinite' }}/>
                                        Panel administrativo
                                    </span>
                                </div>
                                <h1 style={{ fontSize:'clamp(1.6rem,3vw,2.2rem)', fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing:'-.03em', lineHeight:1.1, marginBottom:'6px' }}>
                                    Dashboard <span style={{ color:colors.primary }}>Financiero</span>
                                </h1>
                                <p style={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize:'.88rem', fontWeight:'500' }}>
                                    Cobros registrados, deuda y medios de pago · {getNombreSucursal()}
                                </p>
                                <div style={{ display:'flex', gap:'5px', marginTop:'12px' }}>
                                    {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                        <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                    ))}
                                </div>
                            </div>

                            <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'10px', background: isDark ? '#1c0e35' : '#f8fafc', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9', borderRadius:'12px', padding:'8px 14px' }}>
                                    <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`linear-gradient(135deg,${colors.secondary},${colors.secondaryLight})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                        <User size={14} color="#fff"/>
                                    </div>
                                    <div>
                                        <p style={{ fontSize:'.65rem', color:'#94a3b8', fontWeight:'600', textTransform:'uppercase', letterSpacing:'.05em' }}>Usuario</p>
                                        <p style={{ fontSize:'.8rem', fontWeight:'700', color: isDark ? '#ffffff' : '#0f172a' }}>{user ? `${user.first_name} ${user.last_name}` : 'Admin'}</p>
                                    </div>
                                </div>

                                <div ref={selectorRef} style={{ position:'relative' }}>
                                    <button ref={btnSucRef} onClick={handleToggleSuc} style={{ display:'flex', alignItems:'center', gap:'8px', background: isDark ? '#1c0e35' : '#f8fafc', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : (mostrarSelectorSucursal ? colors.secondary+'40' : '#f1f5f9'), borderRadius:'12px', padding:'8px 14px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all .2s' }}>
                                        <Building size={14} color={colors.secondary}/>
                                        <span style={{ fontSize:'.8rem', fontWeight:'700', color: isDark ? '#e2e8f0' : '#374151', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{getNombreSucursal()}</span>
                                        <ChevronDown size={13} color="#94a3b8" style={{ transition:'transform .2s', transform:mostrarSelectorSucursal?'rotate(180deg)':'rotate(0)' }}/>
                                    </button>

                                    {mostrarSelectorSucursal && (
                                        <div className="adm-slidedown" style={{ position:'absolute', top:'calc(100% + 8px)', right:0, width:'240px', background: isDark ? '#180a2e' : '#fff', borderRadius:'16px', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : '1.5px solid #f1f5f9', boxShadow: isDark ? '0 16px 48px rgba(0,0,0,.6)' : '0 16px 48px rgba(0,0,0,.09)', overflow:'hidden', zIndex:9999 }}>
                                            <div style={{ padding:'10px 14px', borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #f1f5f9', background: isDark ? '#140827' : '#f8fafc' }}>
                                                <span style={{ fontSize:'.65rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em' }}>Sucursales activas</span>
                                            </div>
                                            <div className="custom-scroll" style={{ maxHeight:'200px', overflowY:'auto' }}>
                                                {[{ id:'todas', nombre:'Todas las sucursales', codigo:'' }, ...sucursalesActivas.map(s=>({ id:s.id.toString(), nombre:s.nombre, codigo:s.codigo }))].map(s => (
                                                    <button key={s.id} onClick={()=>{ setSucursalSel(s.id); setMostrarSel(false); }} className="adm-row-hover" style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'transparent', border:'none', borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.2)' : '1px solid #f1f5f9', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                                                        <div style={{ textAlign:'left' }}>
                                                            <p style={{ fontSize:'.82rem', fontWeight:'700', color: isDark ? '#ffffff' : '#0f172a' }}>{s.nombre}</p>
                                                            {s.codigo && <p style={{ fontSize:'.7rem', color:'#94a3b8' }}>{s.codigo}</p>}
                                                        </div>
                                                        {sucursalSeleccionada===s.id && <span style={{ width:'8px', height:'8px', borderRadius:'50%', background:colors.secondaryLight, boxShadow:`0 0 0 2px ${colors.secondaryLight}30`, display:'inline-block' }}/>}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'12px', marginBottom:'18px' }}>
                            {[
                                { label:'Desde', value:fechaInicio, onChange:(v:string)=>setFechaInicio(v), max:fechaFin, min:undefined },
                                { label:'Hasta', value:fechaFin,   onChange:(v:string)=>setFechaFin(v),    max:undefined, min:fechaInicio },
                            ].map(f=>(
                                <div key={f.label} style={{ background: isDark ? '#1c0e35' : '#f8fafc', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px' }}>
                                    <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.65rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'8px' }}>
                                        <Calendar size={11} color={colors.secondary}/> {f.label}
                                    </label>
                                    <input type="date" className="adm-input" value={f.value} onChange={e=>f.onChange(e.target.value)} max={f.max} min={f.min} style={{ padding:'6px 10px' }}/>
                                </div>
                            ))}
                            {[
                                { icon:<Clock size={14}/>, label:'Citas hoy', value:stats.citasHoy, isNum:true },
                                { icon:<DollarSign size={14}/>, label:'Pagado hoy', value:formatoMoneda(stats.totalPagadoHoy), isNum:false },
                            ].map((s,i)=>(
                                <div key={i} style={{ background: isDark ? '#1c0e35' : '#f8fafc', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px' }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.65rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'8px' }}>
                                        <span style={{ color:colors.secondary }}>{s.icon}</span> {s.label}
                                    </div>
                                    <p style={{ fontSize:'1.3rem', fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a' }}>{s.value}{s.isNum && <span style={{ fontSize:'.8rem', color:'#94a3b8', fontWeight:'500', marginLeft:'4px' }}>citas</span>}</p>
                                </div>
                            ))}
                        </div>

                        <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                            {[
                                { label:'Hoy',           fn:()=>{ const h=boliviaDateStamp(); setFechaInicio(h); setFechaFin(h); } },
                                { label:'Ayer',          fn:()=>{ const h=parsearFechaDesdeInput(boliviaDateStamp()); const a=subDays(h,1); setFechaInicio(formatDateForInput(a)); setFechaFin(formatDateForInput(a)); } },
                                { label:'Últimos 7 días',fn:()=>{ const h=parsearFechaDesdeInput(boliviaDateStamp()); setFechaInicio(formatDateForInput(subDays(h,7))); setFechaFin(formatDateForInput(h)); } },
                                { label:'Últimos 30 días',fn:()=>{ const h=parsearFechaDesdeInput(boliviaDateStamp()); setFechaInicio(formatDateForInput(subDays(h,30))); setFechaFin(formatDateForInput(h)); } },
                                { label:'Este mes',      fn:()=>{ const h=parsearFechaDesdeInput(boliviaDateStamp()); setFechaInicio(formatDateForInput(startOfMonth(h))); setFechaFin(formatDateForInput(h)); } },
                            ].map(b=>(
                                <button key={b.label} onClick={b.fn} className="adm-pill-btn">{b.label}</button>
                            ))}
                        </div>
                    </div>
                </div>

                {paymentError && (
                    <div role="alert" style={{ marginBottom:'20px', padding:'12px 16px', borderRadius:'12px', background:'#fff5f6', border:`1px solid ${colors.primary}25`, color:colors.primaryDark, fontWeight:'700', fontSize:'.82rem' }}>
                        {paymentError}
                    </div>
                )}

                {/* ── MÉTRICAS ── */}
                <div className="adm-fadeup-1" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'16px', marginBottom:'28px' }}>
                    {metricCards.map((m,i)=>(
                        <div key={i} className="adm-metric-card" style={{ '--accent':m.accentColor } as React.CSSProperties}>
                            <style>{`.adm-metric-card:nth-child(${i+1})::before { background:${m.accentColor}; }`}</style>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
                                <p style={{ fontSize:'.78rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#64748b', textTransform:'uppercase', letterSpacing:'.06em' }}>{m.label}</p>
                                <div style={{ width:'38px', height:'38px', borderRadius:'12px', background: isDark ? `linear-gradient(135deg,${m.accentColor}30,${m.accentColor}10)` : `linear-gradient(135deg,${m.gradFrom},${m.gradTo})`, display:'flex', alignItems:'center', justifyContent:'center', color:m.accentColor, flexShrink:0 }}>
                                    {m.icon}
                                </div>
                            </div>
                            <p style={{ fontSize:'clamp(1.2rem,2vw,1.5rem)', fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing:'-.02em', marginBottom:'10px' }}>{m.value}</p>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', background:`${m.accentColor}12`, border:`1px solid ${m.accentColor}25`, borderRadius:'100px', padding:'3px 10px', fontSize:'.68rem', fontWeight:'700', color:m.accentColor }}>
                                {m.sub}
                            </span>
                        </div>
                    ))}
                </div>

                {/* ── GRÁFICOS ── */}
                <div className="adm-fadeup-2" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:'20px', marginBottom:'28px' }}>
                    {/* Flujo financiero por fecha efectiva del pago */}
                    <div className="adm-card" style={{ padding:'24px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
                            <div>
                                <h3 style={{ fontSize:'1rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a' }}>Flujo de cobros por día</h3>
                                <p style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:'2px' }}>{getNombreSucursal()} · Según fecha de cada pago</p>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', background:`${colors.secondary}10`, border:`1px solid ${colors.secondary}20`, borderRadius:'100px', padding:'5px 12px' }}>
                                <BarChart2 size={12} color={colors.secondary}/>
                                <span style={{ fontSize:'.7rem', fontWeight:'700', color:colors.secondary }}>Tendencia</span>
                            </div>
                        </div>
                        {metrica.ingresosPorDia.length > 0 ? (
                            <div style={{ height:'260px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={metrica.ingresosPorDia}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(193, 52, 139, 0.15)' : '#f1f5f9'}/>
                                        <XAxis dataKey="fecha" angle={-45} textAnchor="end" height={55} tick={{ fontSize:10, fill: isDark ? '#a0aec0' : '#94a3b8', fontFamily:"'DM Sans',sans-serif" }}/>
                                        <YAxis tick={{ fontSize:10, fill: isDark ? '#a0aec0' : '#94a3b8', fontFamily:"'DM Sans',sans-serif" }}/>
                                        <Tooltip content={<TooltipBarra isDark={isDark} />}/>
                                        <Legend verticalAlign="top" height={36} iconType="circle" formatter={(v: string) => <span style={{ fontSize:'.72rem', color: isDark ? '#cbd5e1' : '#374151', fontFamily:"'DM Sans',sans-serif" }}>{v}</span>} />
                                        <Bar dataKey="ingresos" name="Cobrado" fill={colors.secondary} radius={[8,8,0,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div style={{ height:'260px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color: isDark ? '#64748b' : '#cbd5e1' }}>
                                <BarChart2 size={40} style={{ opacity:.3, marginBottom:'10px' }}/>
                                <p style={{ fontSize:'.82rem' }}>Sin ingresos en este período</p>
                            </div>
                        )}
                    </div>

                    {/* Composición financiera por origen canónico */}
                    <div className="adm-card" style={{ padding:'24px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
                            <div>
                                <h3 style={{ fontSize:'1rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a' }}>Composición por origen</h3>
                                <p style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:'2px' }}>{getNombreSucursal()} · Reservas, sesiones, cafetería y caja</p>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', background:`${colors.accentDark}10`, border:`1px solid ${colors.accentDark}20`, borderRadius:'100px', padding:'5px 12px' }}>
                                <PieChart size={12} color={colors.accentDark}/>
                                <span style={{ fontSize:'.7rem', fontWeight:'700', color:colors.accentDark }}>Composición</span>
                            </div>
                        </div>
                        {metrica.distribucionOrigen.length > 0 ? (
                            <div style={{ height:'260px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <RechartsPieChart>
                                        <Pie data={metrica.distribucionOrigen} cx="50%" cy="50%" labelLine={false} label={({percent})=>`${(percent*100).toFixed(0)}%`} outerRadius={90} innerRadius={45} paddingAngle={3} dataKey="value">
                                            {metrica.distribucionOrigen.map((e,i)=>(
                                                <Cell key={`c-${i}`} fill={e.color} stroke={isDark ? '#150a28' : 'white'} strokeWidth={2}/>
                                            ))}
                                        </Pie>
                                        <Tooltip content={<TooltipPie isDark={isDark} />}/>
                                        <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" formatter={(v)=><span style={{ fontSize:'.72rem', color: isDark ? '#cbd5e1' : '#374151', fontFamily:"'DM Sans',sans-serif" }}>{v}</span>}/>
                                    </RechartsPieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div style={{ height:'260px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color: isDark ? '#64748b' : '#cbd5e1' }}>
                                <PieChart size={40} style={{ opacity:.3, marginBottom:'10px' }}/>
                                <p style={{ fontSize:'.82rem' }}>Sin pagos en este período</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── TABLAS ── */}
                <div className="adm-fadeup-3" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:'20px', marginBottom:'28px' }}>
                    {/* Pagos canónicos del período */}
                    <div className="adm-card" style={{ overflow:'hidden' }}>
                        <div style={{ padding:'20px 24px', borderBottom: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
                                <div>
                                    <h3 style={{ fontSize:'1rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a' }}>Pagos del período</h3>
                                    <p style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:'2px' }}>{getNombreSucursal()} · {pagosRecientes.length} pagos</p>
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                                    <Filter size={12} color="#94a3b8"/>
                                    <span style={{ fontSize:'.7rem', color:'#94a3b8' }}>Recientes</span>
                                </div>
                            </div>
                            <div style={{ position:'relative' }}>
                                <Search size={14} color="#94a3b8" style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
                                <input type="text" placeholder="Buscar por pago, reserva, cliente u origen..." value={busqueda} onChange={e=>setBusqueda(e.target.value)} className="adm-input" style={{ paddingLeft:'36px' }}/>
                            </div>
                        </div>
                        <div className="custom-scroll" style={{ maxHeight:'400px', overflowY:'auto' }}>
                            {pagosRecientes.filter(p=>{
                                if (!busqueda) return true;
                                const text = [p.code,p.sourceCode,p.customer,p.paymentType,p.origin,p.branch].filter(Boolean).join(' ').toLowerCase();
                                return text.includes(busqueda.toLowerCase());
                            }).map(pago=>(
                                    <div key={pago.id} className="adm-row-hover" style={{ padding:'14px 20px', borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.15)' : '1px solid #f8fafc', display:'flex', gap:'12px', alignItems:'flex-start' }}>
                                        <div style={{ flex:1, minWidth:0 }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', marginBottom:'6px' }}>
                                                <span style={{ fontFamily:'monospace', fontSize:'.72rem', background: isDark ? '#1c0e35' : '#f1f5f9', color: isDark ? '#e2e8f0' : '#374151', padding:'2px 8px', borderRadius:'6px' }}>{pago.code}</span>
                                                <span style={{ display:'inline-flex', padding:'3px 10px', borderRadius:'100px', fontSize:'.68rem', fontWeight:'700', background:`${colors.secondary}12`, color:colors.secondary, border:`1px solid ${colors.secondary}25` }}>{pago.origin}</span>
                                            </div>
                                            <p style={{ fontWeight:'700', color: isDark ? '#ffffff' : '#0f172a', fontSize:'.85rem', marginBottom:'3px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pago.customer || 'Sin cliente asociado'} · {pago.paymentType}</p>
                                            <p style={{ fontSize:'.72rem', color:'#94a3b8', display:'flex', alignItems:'center', gap:'4px' }}>
                                                <Calendar size={10}/> {formatoFechaLocal(pago.paidAt)} {pago.sourceCode && `· ${pago.sourceCode}`}
                                            </p>
                                        </div>
                                        <div style={{ textAlign:'right', flexShrink:0 }}>
                                            <p style={{ fontWeight:'900', fontSize:'1rem', color: isDark ? '#ffffff' : '#0f172a' }}>{formatoMoneda(Number(pago.amount))}</p>
                                            <p style={{ fontSize:'.7rem', color:colors.secondaryLight, fontWeight:'600', marginTop:'2px' }}>{pago.branch || 'Sin sucursal'}</p>
                                        </div>
                                    </div>
                            ))}
                            {pagosRecientes.length===0 && (
                                <div style={{ padding:'40px', textAlign:'center', color: isDark ? '#64748b' : '#cbd5e1' }}>
                                    <DollarSign size={32} style={{ margin:'0 auto 10px', opacity:.3 }}/>
                                    <p style={{ fontSize:'.85rem' }}>Sin transacciones recientes</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Clientes frecuentes */}
                    <div className="adm-card" style={{ overflow:'hidden' }}>
                        <div style={{ padding:'20px 24px', borderBottom: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                                <div>
                                    <h3 style={{ fontSize:'1rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a' }}>Clientes con Mayor Retorno</h3>
                                    <p style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:'2px' }}>Frecuencia de visitas</p>
                                </div>
                                <button onClick={loadClientes} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', padding:'6px', borderRadius:'8px', transition:'background .15s' }}
                                    onMouseEnter={e=>e.currentTarget.style.background=isDark ? 'rgba(193, 52, 139, 0.15)' : '#f1f5f9'} onMouseLeave={e=>e.currentTarget.style.background='none'}>
                                    <RefreshCcw size={15} color={colors.secondary} style={{ animation:loadingClientes?'spin .7s linear infinite':undefined }}/>
                                </button>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.75rem', color:'#94a3b8' }}>
                                <Target size={12}/> Clientes con más visitas
                            </div>
                        </div>
                        <div className="custom-scroll" style={{ maxHeight:'400px', overflowY:'auto' }}>
                            {clientesFrecuentes.map((cl,i)=>(
                                <div key={cl.cliente_id} className="adm-row-hover" style={{ padding:'14px 20px', borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.15)' : '1px solid #f8fafc' }}>
                                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                            <div style={{ width:'38px', height:'38px', borderRadius:'12px', background:`linear-gradient(135deg,${[colors.primary,colors.secondary,colors.secondaryLight,colors.accentDark,colors.accent][i]},${[colors.primaryDark,colors.secondary+'cc',colors.secondaryLight+'cc',colors.accentDark+'cc',colors.accent+'cc'][i]})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:'900', fontSize:'.88rem', flexShrink:0 }}>
                                                {cl.cliente_nombre?.charAt(0).toUpperCase()||'C'}
                                            </div>
                                            <div>
                                                <p style={{ fontWeight:'700', color: isDark ? '#ffffff' : '#0f172a', fontSize:'.85rem' }}>{cl.cliente_nombre||'Cliente'}</p>
                                                <p style={{ fontSize:'.7rem', color:'#94a3b8', display:'flex', alignItems:'center', gap:'3px' }}>
                                                    <Calendar size={10}/> {formatoFechaCorta(cl.ultima_cita_inicio)}
                                                </p>
                                            </div>
                                        </div>
                                        <span style={{ background:`${colors.secondaryLight}18`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                            {cl.cantidad_citas} visitas
                                        </span>
                                    </div>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                                        <div style={{ background: isDark ? '#1c0e35' : '#f8fafc', borderRadius:'10px', padding:'8px 10px' }}>
                                            <p style={{ fontSize:'.65rem', color:'#94a3b8', fontWeight:'600', marginBottom:'2px' }}>Frecuencia promedio</p>
                                            <p style={{ fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', fontSize:'.82rem' }}>{cl.frecuencia_promedio_dias.toFixed(1)} días</p>
                                        </div>
                                        <div style={{ background: isDark ? '#1c0e35' : '#f8fafc', borderRadius:'10px', padding:'8px 10px' }}>
                                            <p style={{ fontSize:'.65rem', color:'#94a3b8', fontWeight:'600', marginBottom:'2px' }}>Categoría</p>
                                            <span style={{ display:'inline-block', background:`${colors.secondary}12`, border:`1px solid ${colors.secondary}22`, color:colors.secondary, borderRadius:'6px', padding:'2px 8px', fontSize:'.68rem', fontWeight:'700' }}>
                                                {cl.categoria_retorno||'Frecuente'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {clientesFrecuentes.length===0 && (
                                <div style={{ padding:'40px', textAlign:'center', color: isDark ? '#64748b' : '#cbd5e1' }}>
                                    <Users size={32} style={{ margin:'0 auto 10px', opacity:.3 }}/>
                                    <p style={{ fontSize:'.85rem' }}>Sin clientes frecuentes</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── COBROS POR MEDIO DE PAGO ── */}
                <div className="adm-card adm-fadeup-4" style={{ padding:'24px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
                        <div>
                            <h3 style={{ fontSize:'1rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a' }}>Cobros por medio de pago</h3>
                            <p style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:'2px' }}>{getNombreSucursal()} · Conciliación del período seleccionado</p>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', background:`${colors.secondary}10`, border:`1px solid ${colors.secondary}20`, borderRadius:'100px', padding:'5px 14px' }}>
                            <Banknote size={12} color={colors.secondary}/>
                            <span style={{ fontSize:'.7rem', fontWeight:'700', color:colors.secondary }}>{financials.paymentCount} pagos</span>
                        </div>
                    </div>
                    {financials.byPaymentType.length > 0 ? (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:'14px' }}>
                            {financials.byPaymentType.map((method,index)=>(
                                <div key={method.label} style={{ background: isDark ? '#1a0d30' : '#f8fafc', borderRadius:'16px', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9', padding:'18px' }}>
                                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                                        <span style={{ fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', fontSize:'.85rem' }}>{method.label}</span>
                                        <span style={{ width:'9px', height:'9px', borderRadius:'50%', background:[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight][index % 4] }}/>
                                    </div>
                                    <p style={{ fontWeight:'900', color:'#0f172a', fontSize:'1.15rem' }}>{formatoMoneda(Number(method.amount))}</p>
                                    <p style={{ color:'#64748b', fontSize:'.72rem', marginTop:'5px' }}>{method.count} {method.count === 1 ? 'pago' : 'pagos'}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ padding:'42px 0', textAlign:'center', color: isDark ? '#64748b' : '#cbd5e1' }}>
                            <Banknote size={38} style={{ margin:'0 auto 10px', opacity:.3 }}/>
                            <p style={{ fontSize:'.85rem' }}>Sin cobros registrados en este período</p>
                        </div>
                    )}
                </div>

            </div>
        </>
    );
};

export default StatsPanel;
